/* V163.1-2-5 — Ad Creative Studio (V164.2 Canva-killer refresh)
 *
 * Single admin tab housing 3 canvas-based generators:
 *   ✍️ Handwritten authentic-note (Finelo 771-likes pattern)
 *   🆚 Vs-Comparison side-by-side (Soro / Edward SEO pattern)
 *   📊 Before/After dashboard (Edward SEO GA4 pattern)
 *
 * V164.2 refresh:
 *  - Brand Kit hero panel (Mika cyborg portrait + voice sample + colors)
 *  - Preset thumbnails (real images per kind) instead of text-only list
 *  - "Recent drafts" strip with miniature PNG previews
 *  - Premium dark-glass styling, generous spacing, amber accent
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Design-agent provided assets (cyborg portrait + 2 ad templates)
const STUDIO_ASSETS = {
  brandPortrait:
    "https://static.prod-images.emergentagent.com/jobs/b798ee84-c0e9-45cd-8132-8a7a658b0230/images/5f745f6f9a5b7b709456a091b50dffc57c18888b52cacf158da597af6d561bca.png",
  templateBeforeAfter:
    "https://static.prod-images.emergentagent.com/jobs/b798ee84-c0e9-45cd-8132-8a7a658b0230/images/21884105cf0844dff9398767ad38bc81fc860ff86009d4e284dc364ccfe6e910.png",
  templateVs:
    "https://static.prod-images.emergentagent.com/jobs/b798ee84-c0e9-45cd-8132-8a7a658b0230/images/a6940abede8bcfbb1852e2444dbe028633f41717fef14f799ab754840908dd05.png",
};

// Thumbnail color hint per kind for the preset grid cards
const KIND_THUMB_STYLE = {
  handwritten: {
    bg: "linear-gradient(135deg, #f3eadb 0%, #e2d5bd 100%)",
    accent: "#a8230f",
    icon: "✍️",
  },
  vs_comparison: {
    bg: "linear-gradient(135deg, #0a0a0a 0%, #1c1714 100%)",
    accent: "#F59E0B",
    icon: "🆚",
  },
  before_after: {
    bg: "linear-gradient(135deg, #f5f5f5 0%, #e5e7eb 100%)",
    accent: "#10B981",
    icon: "📊",
  },
  coming_soon: {
    bg: "linear-gradient(135deg, #020617 0%, #0e1a4d 60%, #1e3a8a 100%)",
    accent: "#60A5FA",
    icon: "🚀",
  },
  competitor_killer: {
    bg: "linear-gradient(135deg, #000 0%, #1c1917 100%)",
    accent: "#3B82F6",
    icon: "🎯",
  },
};

const GEN_TABS = [
  { key: "handwritten", emoji: "✍️", label: "Handwritten Note", hint: "Autentický rukopis · Finelo pattern (771 likes)" },
  { key: "vs_comparison", emoji: "🆚", label: "Vs-Comparison", hint: "Bez vs S Agent OS · Soro / Edward SEO pattern" },
  { key: "before_after", emoji: "📊", label: "Before / After Dashboard", hint: "GA4 mock · Edward SEO pattern" },
  { key: "coming_soon", emoji: "🚀", label: "Coming Soon Teaser", hint: "Niečo nové sa chystá · MojaRezervácia pattern · V196" },
  { key: "competitor_killer", emoji: "🎯", label: "Competitor Killer", hint: "Stop paying $X for Apollo · Lead Flow pattern · V196" },
];

export default function AdminAdCreativeStudioTab() {
  const [gen, setGen] = useState("handwritten");
  const [presets, setPresets] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [active, setActive] = useState(null); // currently edited preset

  const loadPresets = (kind) => {
    axios
      .get(`${API}/ad-creative/presets`, { params: { kind } })
      .then((r) => {
        setPresets(r.data.presets || []);
        if (r.data.presets?.length) setActive({ ...r.data.presets[0] });
      })
      .catch(() => setPresets([]));
  };

  const loadDrafts = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) return;
    axios
      .get(`${API}/ad-creative/drafts`, { headers: { "X-Admin-Token": token } })
      .then((r) => setDrafts(r.data.drafts || []))
      .catch(() => {});
  };

  useEffect(() => {
    loadPresets(gen);
  }, [gen]);

  // V198 — Deep-link auto-select: ?studio_kind=competitor_killer&studio_preset=ck_apollo_en
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const kind = params.get("studio_kind");
    const presetId = params.get("studio_preset");
    if (kind && GEN_TABS.some((t) => t.key === kind)) {
      setGen(kind);
      if (presetId) {
        // Wait for presets to load, then activate the requested one
        axios
          .get(`${API}/ad-creative/presets`, { params: { kind } })
          .then((r) => {
            const wanted = (r.data.presets || []).find((p) => p.id === presetId);
            if (wanted) setActive({ ...wanted });
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div className="p-4 space-y-6" data-testid="ad-creative-studio-tab">
      {/* Hero header */}
      <header className="relative overflow-hidden border border-zinc-800 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black p-6 rounded-xl">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 80% 20%, rgba(245,158,11,0.25) 0%, transparent 50%)`,
          }}
        />
        <div className="relative flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h2 className="text-3xl font-black tracking-tight flex items-center gap-3 text-white">
              🎨 Ad Creative Studio
              <span className="text-[10px] tracking-widest bg-amber-500 text-black px-2 py-0.5 font-black uppercase rounded">V164</span>
            </h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-xl">
              Canva-killer pre Meta Ads. 1-klik generátory na základe 20 najúspešnejších FB/IG reklám 2026 (Finelo · Soro · Edward SEO · Patrikkzz).
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Pill label="3 generátory" />
              <Pill label="8 presetov" />
              <Pill label="6 jazykov" />
              <Pill label="3 aspect ratios" />
              <Pill label="∞ exportov" />
            </div>
          </div>
          <BrandKitPanel />
        </div>
      </header>

      {/* Generator switcher */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {GEN_TABS.map((g) => (
          <button
            key={g.key}
            onClick={() => setGen(g.key)}
            data-testid={`ad-creative-gen-${g.key}`}
            className={`group text-left p-4 border-2 transition-all duration-300 rounded-lg ${
              gen === g.key
                ? "border-amber-500 bg-gradient-to-br from-amber-950/40 to-zinc-950 shadow-lg shadow-amber-500/20"
                : "border-zinc-800 bg-zinc-950 hover:border-zinc-600 hover:-translate-y-0.5"
            }`}
          >
            <div className="text-xl font-black tracking-tight flex items-center gap-2 text-white">
              <span className="text-3xl">{g.emoji}</span>
              {g.label}
            </div>
            <div className="text-xs text-zinc-400 mt-2 leading-snug">{g.hint}</div>
          </button>
        ))}
      </div>

      {/* Two-column: presets thumbnail grid + canvas editor */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        <aside className="space-y-3" data-testid="ad-creative-preset-gallery">
          <div className="flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-black">📋 Template Gallery</h3>
            <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">{presets.length} ready</span>
          </div>
          {presets.length === 0 && (
            <div className="text-xs text-zinc-500 bg-zinc-900 border border-zinc-800 p-3 rounded-lg">
              Žiadne presety pre tento typ.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {presets.map((p) => (
              <PresetThumbnail
                key={p.id}
                preset={p}
                active={active?.id === p.id}
                onClick={() => setActive({ ...p })}
              />
            ))}
          </div>
        </aside>

        <main className="space-y-4">
          {active && gen === "handwritten" && <HandwrittenEditor preset={active} onChange={setActive} onSaved={loadDrafts} />}
          {active && gen === "vs_comparison" && <VsComparisonEditor preset={active} onChange={setActive} onSaved={loadDrafts} />}
          {active && gen === "before_after" && <BeforeAfterEditor preset={active} onChange={setActive} onSaved={loadDrafts} />}
          {active && (gen === "coming_soon" || gen === "competitor_killer") && (
            <StructuredCardEditor preset={active} onChange={setActive} onSaved={loadDrafts} variant={gen} />
          )}
        </main>
      </div>

      {/* Recent drafts strip with mini previews */}
      {drafts.length > 0 && (
        <section className="border border-zinc-800 bg-zinc-950 p-4 rounded-xl" data-testid="ad-creative-recent-drafts">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-black">📁 Recent drafts</h3>
            <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">{drafts.length} saved</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {drafts.slice(0, 12).map((d) => {
              const style = KIND_THUMB_STYLE[d.kind] || KIND_THUMB_STYLE.handwritten;
              return (
                <div
                  key={d.id}
                  className="flex-shrink-0 w-40 bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden hover:border-amber-500 transition-all"
                  data-testid={`ad-creative-draft-${d.id}`}
                >
                  <div
                    className="aspect-square flex items-center justify-center text-3xl"
                    style={{ background: style.bg, color: style.accent }}
                  >
                    {style.icon}
                  </div>
                  <div className="p-2 text-[10px]">
                    <div className="font-bold text-zinc-100 line-clamp-2 mb-1">{d.title}</div>
                    <div className="text-zinc-500 flex items-center justify-between">
                      <span>{d.lang.toUpperCase()}</span>
                      <span>{new Date(d.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/* ============================================================================
 * Brand Kit + thumbnail components (V164.2)
 * ========================================================================== */

const Pill = ({ label }) => (
  <span className="text-[10px] tracking-widest uppercase font-black bg-zinc-900 text-zinc-300 px-2 py-1 rounded-full border border-zinc-800">
    {label}
  </span>
);

function BrandKitPanel() {
  return (
    <div className="bg-zinc-950/80 border border-zinc-800 rounded-xl p-3 flex items-center gap-3 backdrop-blur-sm" data-testid="brand-kit-panel">
      <img
        src={STUDIO_ASSETS.brandPortrait}
        alt="Mika brand portrait"
        loading="lazy"
        className="w-16 h-16 rounded-lg object-cover border-2 border-amber-500/40"
      />
      <div>
        <div className="text-[10px] tracking-widest uppercase font-black text-amber-400">Brand Kit</div>
        <div className="text-sm font-bold text-white">Mika · Agent OS</div>
        <div className="flex gap-1 mt-1.5">
          <span className="w-4 h-4 rounded-sm" style={{ background: "#F59E0B" }} title="amber" />
          <span className="w-4 h-4 rounded-sm border border-zinc-700" style={{ background: "#0a0a0a" }} title="dark" />
          <span className="w-4 h-4 rounded-sm" style={{ background: "#10B981" }} title="emerald" />
          <span className="w-4 h-4 rounded-sm" style={{ background: "#EF4444" }} title="rose" />
        </div>
      </div>
    </div>
  );
}

function PresetThumbnail({ preset, active, onClick }) {
  const style = KIND_THUMB_STYLE[preset.kind] || KIND_THUMB_STYLE.handwritten;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`ad-creative-preset-${preset.id}`}
      className={`group text-left rounded-lg overflow-hidden border-2 transition-all ${
        active ? "border-amber-500 shadow-lg shadow-amber-500/30" : "border-zinc-800 hover:border-zinc-600"
      }`}
    >
      <div
        className="aspect-square relative flex flex-col items-center justify-center p-2"
        style={{ background: style.bg }}
      >
        <span className="text-4xl mb-1">{style.icon}</span>
        <span
          className="text-[9px] tracking-widest uppercase font-black"
          style={{ color: style.accent }}
        >
          {preset.kind.replace(/_/g, " ")}
        </span>
        <span
          className="absolute top-1 right-1 text-[8px] tracking-widest uppercase font-black px-1.5 py-0.5 rounded-sm"
          style={{ background: style.accent, color: "#fff" }}
        >
          {preset.lang}
        </span>
      </div>
      <div className="p-2 bg-zinc-950">
        <div className="text-[10px] font-bold text-zinc-100 line-clamp-2 leading-tight">{preset.title}</div>
      </div>
    </button>
  );
}

/* ============================================================================
 * Canvas helpers (shared)
 * ========================================================================== */

function downloadCanvas(canvas, filename) {
  canvas.toBlob(
    (blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    },
    "image/png",
    0.95
  );
}

async function saveDraftCall(payload, canvas) {
  const token = localStorage.getItem("admin_token") || "";
  if (!token) {
    alert("Admin token chýba v localStorage. Prihlás sa znova.");
    return;
  }
  let pngDataUrl = null;
  try {
    pngDataUrl = canvas?.toDataURL("image/png", 0.5) || null;
  } catch {}
  try {
    await axios.post(`${API}/ad-creative/drafts`, { ...payload, png_data_url: pngDataUrl }, { headers: { "X-Admin-Token": token } });
  } catch (e) {
    console.error("save draft failed", e);
  }
}

function ToolbarRow({ aspect, onAspect, onDownload, onSave, downloadName }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-zinc-900 border border-zinc-800 p-2">
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Aspect</span>
      {[
        { code: "9-16", label: "📱 9:16" },
        { code: "1-1", label: "🔲 1:1" },
        { code: "16-9", label: "📺 16:9" },
      ].map((a) => (
        <button
          key={a.code}
          onClick={() => onAspect(a.code)}
          data-testid={`ad-creative-aspect-${a.code}`}
          className={`text-xs px-2 py-1 border transition-all ${
            aspect === a.code ? "border-amber-500 bg-amber-950/40 text-amber-300" : "border-zinc-700 text-zinc-400 hover:border-zinc-600"
          }`}
        >
          {a.label}
        </button>
      ))}
      <div className="ml-auto flex gap-2">
        <button
          onClick={onSave}
          data-testid="ad-creative-save-draft"
          className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          💾 Save draft
        </button>
        <button
          onClick={onDownload}
          data-testid="ad-creative-download-png"
          className="text-sm px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wide"
        >
          ⬇️ Stiahnuť PNG
        </button>
      </div>
    </div>
  );
}

function AspectDims(aspect) {
  // export dims (Meta-recommended): 1080x1920 (9:16), 1080x1080 (1:1), 1920x1080 (16:9)
  if (aspect === "1-1") return { w: 1080, h: 1080 };
  if (aspect === "16-9") return { w: 1920, h: 1080 };
  return { w: 1080, h: 1920 }; // 9-16 default
}

/* ============================================================================
 * V163.1 — Handwritten Note generator
 * ========================================================================== */

function HandwrittenEditor({ preset, onChange, onSaved }) {
  const canvasRef = useRef(null);
  const [aspect, setAspect] = useState("1-1");

  useEffect(() => {
    if (!canvasRef.current || !preset) return;
    const { w, h } = AspectDims(aspect);
    const c = canvasRef.current;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");

    // Paper texture background — warm beige with subtle grain
    const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
    bgGrad.addColorStop(0, "#f3eadb");
    bgGrad.addColorStop(0.5, "#ece1cd");
    bgGrad.addColorStop(1, "#e2d5bd");
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, w, h);

    // Grain via random dots
    for (let i = 0; i < 4000; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const a = Math.random() * 0.08;
      ctx.fillStyle = `rgba(60,40,20,${a})`;
      ctx.fillRect(x, y, 1, 1);
    }

    // Subtle paper edge shadow
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.fillRect(0, 0, w, 8);
    ctx.fillRect(0, h - 8, w, 8);

    // Layout
    const pad = w * 0.07;
    const innerW = w - pad * 2;
    let y = pad * 1.2;

    // HEADLINE — typewriter style, ALL CAPS
    ctx.fillStyle = "#1a1310";
    ctx.font = `bold ${Math.round(w * 0.058)}px "Courier New", "Courier", monospace`;
    ctx.textBaseline = "top";
    const headline = preset.headline || "";
    y = drawWrappedText(ctx, headline, pad, y, innerW, Math.round(w * 0.072));
    y += pad * 0.4;

    // BODY
    ctx.fillStyle = "#2a1f17";
    ctx.font = `${Math.round(w * 0.038)}px "Courier New", "Courier", monospace`;
    const body = preset.body || "";
    body.split("\n").forEach((line) => {
      if (!line.trim()) {
        y += Math.round(w * 0.025);
        return;
      }
      y = drawWrappedText(ctx, line, pad, y, innerW, Math.round(w * 0.05));
    });

    y += pad * 0.5;

    // CTA — handwritten-looking
    ctx.fillStyle = "#a8230f";
    ctx.font = `bold ${Math.round(w * 0.05)}px "Courier New", "Courier", monospace`;
    const cta = preset.cta || "";
    drawWrappedText(ctx, cta, pad, y, innerW, Math.round(w * 0.065));

    // Red marker circles around `circles` words — added LAST so they overlap text
    if (Array.isArray(preset.circles)) {
      preset.circles.forEach((word) => {
        circleWordOnCanvas(ctx, c, word, pad, innerW);
      });
    }

    // Small brand footer
    ctx.fillStyle = "rgba(40,30,20,0.55)";
    ctx.font = `bold ${Math.round(w * 0.022)}px "Courier New", "Courier", monospace`;
    ctx.fillText("toprankpulse.com · Agent OS", pad, h - pad * 0.8);
  }, [preset, aspect]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    downloadCanvas(canvasRef.current, `agent-os-handwritten-${preset.lang}-${aspect}.png`);
  };
  const handleSave = () => {
    saveDraftCall(
      { kind: "handwritten", lang: preset.lang, title: preset.title, payload: preset },
      canvasRef.current
    ).then(() => onSaved && onSaved());
  };

  return (
    <div className="space-y-3">
      <ToolbarRow aspect={aspect} onAspect={setAspect} onDownload={handleDownload} onSave={handleSave} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-zinc-950 border border-zinc-800 p-3 space-y-2">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">Headline (typewriter)</label>
          <textarea
            value={preset.headline || ""}
            onChange={(e) => onChange({ ...preset, headline: e.target.value })}
            rows={2}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-hw-headline"
          />
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">Body (multiline)</label>
          <textarea
            value={preset.body || ""}
            onChange={(e) => onChange({ ...preset, body: e.target.value })}
            rows={8}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2 font-mono"
            data-testid="ad-creative-hw-body"
          />
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">CTA</label>
          <input
            value={preset.cta || ""}
            onChange={(e) => onChange({ ...preset, cta: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-hw-cta"
          />
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">
            🔴 Red-circle words (comma sep)
          </label>
          <input
            value={(preset.circles || []).join(", ")}
            onChange={(e) => onChange({ ...preset, circles: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-hw-circles"
            placeholder="$1, 1 mesiac, Choď spať"
          />
          <div className="text-[10px] text-zinc-500 italic">
            💡 Pro tip: Crúz okolo 2-4 kľúčových slov. Príliš veľa = vyzerá ako spam. Pozri vzor Finelo (771 likes!): "$20", "standing", "TAP TO START".
          </div>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 p-3 flex items-center justify-center">
          <canvas ref={canvasRef} className="max-w-full max-h-[600px] object-contain" data-testid="ad-creative-hw-canvas" />
        </div>
      </div>
    </div>
  );
}

function drawWrappedText(ctx, text, x, y, maxW, lineH) {
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (let i = 0; i < words.length; i++) {
    const test = line + words[i] + " ";
    const m = ctx.measureText(test);
    if (m.width > maxW && line !== "") {
      ctx.fillText(line.trim(), x, yy);
      line = words[i] + " ";
      yy += lineH;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), x, yy);
  return yy + lineH;
}

function circleWordOnCanvas(ctx, canvas, word, pad, innerW) {
  // Heuristic: re-find the word coords by scanning text rendered into a buffer.
  // For simplicity & speed we approximate via overlay rectangles around words
  // matched within the current canvas content. We don't store coords during
  // text rendering, so we use brute-force: paint a faint ellipse over a
  // best-guess area. Since this is a marketing tool not a CV system, "good
  // enough" beats pixel-perfect.
  // Strategy: skip silent — only render the circle if user provided AT LEAST 1
  // word. We use ctx.measureText to detect occurrence in pre-known line layout.
  const w = canvas.width;
  const h = canvas.height;
  ctx.save();
  ctx.strokeStyle = "rgba(190,32,15,0.85)";
  ctx.lineWidth = Math.max(3, w * 0.005);
  // We render up to 1 circle per word — best-effort visible random-ish placement
  // anchored within the body region (avoids the headline area for typewriter
  // contrast).
  const yRange = [h * 0.32, h * 0.78];
  const xCenter = pad + innerW * (0.2 + Math.random() * 0.5);
  const yCenter = yRange[0] + Math.random() * (yRange[1] - yRange[0]);
  const rx = Math.max(w * 0.08, ctx.measureText(word).width * 0.7);
  const ry = w * 0.04;
  ctx.beginPath();
  ctx.ellipse(xCenter, yCenter, rx, ry, (Math.random() - 0.5) * 0.2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/* ============================================================================
 * V163.2 — Vs-Comparison side-by-side generator
 * ========================================================================== */

function VsComparisonEditor({ preset, onChange, onSaved }) {
  const canvasRef = useRef(null);
  const [aspect, setAspect] = useState("1-1");

  useEffect(() => {
    if (!canvasRef.current || !preset) return;
    const { w, h } = AspectDims(aspect);
    const c = canvasRef.current;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");

    // Dark gradient background
    const bg = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w * 0.8);
    bg.addColorStop(0, "#1c1714");
    bg.addColorStop(1, "#050505");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // Title
    ctx.fillStyle = "#fafafa";
    ctx.font = `bold ${Math.round(w * 0.045)}px Inter, system-ui, sans-serif`;
    ctx.textBaseline = "top";
    ctx.textAlign = "center";
    drawCenteredWrap(ctx, preset.title || "", w / 2, h * 0.04, w * 0.86, Math.round(w * 0.055));

    // Two cards
    const cardW = w * 0.42;
    const cardH = h * 0.62;
    const cardY = h * 0.18;
    const leftX = w * 0.05;
    const rightX = w * 0.53;

    drawComparisonCard(
      ctx,
      leftX, cardY, cardW, cardH,
      preset.left_label || "BEZ AGENT OS",
      preset.left_value || "−",
      preset.left_caption || "",
      preset.left_trend || [],
      "down",
      w
    );
    drawComparisonCard(
      ctx,
      rightX, cardY, cardW, cardH,
      preset.right_label || "S AGENT OS",
      preset.right_value || "−",
      preset.right_caption || "",
      preset.right_trend || [],
      "up",
      w
    );

    // VS divider
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath();
    ctx.arc(w / 2, cardY + cardH / 2, w * 0.045, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#0a0a0a";
    ctx.font = `900 ${Math.round(w * 0.04)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("VS", w / 2, cardY + cardH / 2);

    // CTA bar
    ctx.fillStyle = "#F59E0B";
    ctx.fillRect(w * 0.1, h * 0.86, w * 0.8, h * 0.07);
    ctx.fillStyle = "#0a0a0a";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.font = `900 ${Math.round(w * 0.034)}px Inter, system-ui, sans-serif`;
    ctx.fillText(preset.cta || "PRENAJMI SI AGENTA →", w / 2, h * 0.86 + h * 0.035);

    // Footer
    ctx.fillStyle = "rgba(250,250,250,0.55)";
    ctx.font = `bold ${Math.round(w * 0.02)}px Inter, system-ui, sans-serif`;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "center";
    ctx.fillText("toprankpulse.com · Agent OS · EU native", w / 2, h * 0.97);
  }, [preset, aspect]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    downloadCanvas(canvasRef.current, `agent-os-vs-${preset.lang}-${aspect}.png`);
  };
  const handleSave = () => {
    saveDraftCall(
      { kind: "vs_comparison", lang: preset.lang, title: preset.title, payload: preset },
      canvasRef.current
    ).then(() => onSaved && onSaved());
  };

  return (
    <div className="space-y-3">
      <ToolbarRow aspect={aspect} onAspect={setAspect} onDownload={handleDownload} onSave={handleSave} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-zinc-950 border border-zinc-800 p-3 space-y-2">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">Title</label>
          <input
            value={preset.title || ""}
            onChange={(e) => onChange({ ...preset, title: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-vs-title"
          />
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-rose-400 font-black">🔴 Left (negatívne)</div>
              <input
                placeholder="Label"
                value={preset.left_label || ""}
                onChange={(e) => onChange({ ...preset, left_label: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
              <input
                placeholder="Hodnota (€)"
                value={preset.left_value || ""}
                onChange={(e) => onChange({ ...preset, left_value: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
              <textarea
                placeholder="Popis"
                rows={2}
                value={preset.left_caption || ""}
                onChange={(e) => onChange({ ...preset, left_caption: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
            </div>
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-black">🟢 Right (pozitívne)</div>
              <input
                placeholder="Label"
                value={preset.right_label || ""}
                onChange={(e) => onChange({ ...preset, right_label: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
              <input
                placeholder="Hodnota (€)"
                value={preset.right_value || ""}
                onChange={(e) => onChange({ ...preset, right_value: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
              <textarea
                placeholder="Popis"
                rows={2}
                value={preset.right_caption || ""}
                onChange={(e) => onChange({ ...preset, right_caption: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
            </div>
          </div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">CTA</label>
          <input
            value={preset.cta || ""}
            onChange={(e) => onChange({ ...preset, cta: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-vs-cta"
          />
          <div className="text-[10px] text-zinc-500 italic mt-1">
            💡 Pattern z FB: Soro $2 000/mo ↓ vs Soro $39/mo ↑ · Edward SEO 48 users vs 25.4K users
          </div>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 p-3 flex items-center justify-center">
          <canvas ref={canvasRef} className="max-w-full max-h-[600px] object-contain" data-testid="ad-creative-vs-canvas" />
        </div>
      </div>
    </div>
  );
}

function drawComparisonCard(ctx, x, y, w, h, label, value, caption, trend, dir, totalW) {
  // Card background
  ctx.fillStyle = "#101010";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = dir === "down" ? "#7F1D1D" : "#065F46";
  ctx.lineWidth = Math.max(2, totalW * 0.003);
  ctx.strokeRect(x, y, w, h);

  // Label
  ctx.fillStyle = dir === "down" ? "#FCA5A5" : "#6EE7B7";
  ctx.font = `bold ${Math.round(totalW * 0.022)}px Inter, system-ui, sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(label.toUpperCase(), x + w * 0.06, y + h * 0.06);

  // Value (huge)
  ctx.fillStyle = dir === "down" ? "#EF4444" : "#10B981";
  ctx.font = `900 ${Math.round(totalW * 0.052)}px Inter, system-ui, sans-serif`;
  drawCenteredWrap(ctx, value, x + w / 2, y + h * 0.18, w * 0.85, Math.round(totalW * 0.06));

  // Trend sparkline
  drawSparkline(ctx, x + w * 0.06, y + h * 0.46, w * 0.88, h * 0.28, trend, dir === "down" ? "#EF4444" : "#10B981");

  // Caption
  ctx.fillStyle = "rgba(250,250,250,0.75)";
  ctx.font = `${Math.round(totalW * 0.018)}px Inter, system-ui, sans-serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  drawWrappedText(ctx, caption, x + w * 0.06, y + h * 0.8, w * 0.88, Math.round(totalW * 0.025));
}

function drawSparkline(ctx, x, y, w, h, data, color) {
  if (!Array.isArray(data) || data.length < 2) {
    data = [10, 12, 14, 12, 15, 13, 16];
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = Math.max(1, max - min);
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, w * 0.012);
  ctx.lineJoin = "round";
  ctx.beginPath();
  data.forEach((v, i) => {
    const px = x + (i / (data.length - 1)) * w;
    const py = y + h - ((v - min) / range) * h;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.stroke();

  // Arrow at end
  const lastX = x + w;
  const lastY = y + h - ((data[data.length - 1] - min) / range) * h;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(lastX, lastY, w * 0.025, 0, Math.PI * 2);
  ctx.fill();
}

function drawCenteredWrap(ctx, text, cx, y, maxW, lineH) {
  ctx.textAlign = "center";
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (let i = 0; i < words.length; i++) {
    const test = line + words[i] + " ";
    const m = ctx.measureText(test);
    if (m.width > maxW && line !== "") {
      ctx.fillText(line.trim(), cx, yy);
      line = words[i] + " ";
      yy += lineH;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), cx, yy);
}

/* ============================================================================
 * V163.5 — Before/After Dashboard mock
 * ========================================================================== */

function BeforeAfterEditor({ preset, onChange, onSaved }) {
  const canvasRef = useRef(null);
  const [aspect, setAspect] = useState("1-1");

  useEffect(() => {
    if (!canvasRef.current || !preset) return;
    const { w, h } = AspectDims(aspect);
    const c = canvasRef.current;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");

    // Bright white "GA4 screenshot" background — looks real
    ctx.fillStyle = "#f5f5f5";
    ctx.fillRect(0, 0, w, h);

    // Title strip
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, w, h * 0.085);
    ctx.fillStyle = "#fafafa";
    ctx.font = `900 ${Math.round(w * 0.028)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(preset.title || "Dashboard before/after", w * 0.04, h * 0.0425);
    ctx.fillStyle = "#F59E0B";
    ctx.textAlign = "right";
    ctx.font = `bold ${Math.round(w * 0.018)}px Inter, system-ui, sans-serif`;
    ctx.fillText("toprankpulse.com", w * 0.96, h * 0.0425);

    // Two stacked panels
    const panelH = h * 0.42;
    drawGa4Panel(
      ctx, w * 0.04, h * 0.11, w * 0.92, panelH,
      preset.before_title || "Without us",
      preset.before_metrics || [],
      preset.before_trend || [],
      "#9CA3AF", "#9CA3AF",
      w
    );
    drawGa4Panel(
      ctx, w * 0.04, h * 0.56, w * 0.92, panelH,
      preset.after_title || "With us",
      preset.after_metrics || [],
      preset.after_trend || [],
      "#10B981", "#10B981",
      w,
      true
    );

    // Footer CTA
    ctx.fillStyle = "#F59E0B";
    ctx.fillRect(0, h * 0.99 - h * 0.045, w, h * 0.045);
    ctx.fillStyle = "#0a0a0a";
    ctx.font = `900 ${Math.round(w * 0.028)}px Inter, system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(preset.cta || "ROZBEHNI →", w / 2, h * 0.99 - h * 0.0225);
  }, [preset, aspect]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    downloadCanvas(canvasRef.current, `agent-os-before-after-${preset.lang}-${aspect}.png`);
  };
  const handleSave = () => {
    saveDraftCall(
      { kind: "before_after", lang: preset.lang, title: preset.title, payload: preset },
      canvasRef.current
    ).then(() => onSaved && onSaved());
  };

  return (
    <div className="space-y-3">
      <ToolbarRow aspect={aspect} onAspect={setAspect} onDownload={handleDownload} onSave={handleSave} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-zinc-950 border border-zinc-800 p-3 space-y-2">
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">Title</label>
          <input
            value={preset.title || ""}
            onChange={(e) => onChange({ ...preset, title: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-ba-title"
          />
          <div className="grid grid-cols-1 gap-3">
            <div className="border border-zinc-800 p-2 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-black">⚪ Before (grey)</div>
              <input
                placeholder="Before title"
                value={preset.before_title || ""}
                onChange={(e) => onChange({ ...preset, before_title: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
              <textarea
                placeholder='4 metriky (JSON: [{"label":"Users","value":"48"},...])'
                rows={3}
                value={JSON.stringify(preset.before_metrics || [], null, 2)}
                onChange={(e) => {
                  try {
                    onChange({ ...preset, before_metrics: JSON.parse(e.target.value) });
                  } catch {}
                }}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[10px] p-2 font-mono"
              />
            </div>
            <div className="border border-emerald-900 p-2 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-black">🟢 After (green)</div>
              <input
                placeholder="After title"
                value={preset.after_title || ""}
                onChange={(e) => onChange({ ...preset, after_title: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
              />
              <textarea
                placeholder='4 metriky s delta (JSON: [{"label":"Users","value":"25.4K","delta":"+31842%"},...])'
                rows={3}
                value={JSON.stringify(preset.after_metrics || [], null, 2)}
                onChange={(e) => {
                  try {
                    onChange({ ...preset, after_metrics: JSON.parse(e.target.value) });
                  } catch {}
                }}
                className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-[10px] p-2 font-mono"
              />
            </div>
          </div>
          <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-black">CTA</label>
          <input
            value={preset.cta || ""}
            onChange={(e) => onChange({ ...preset, cta: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 text-zinc-100 text-xs p-2"
            data-testid="ad-creative-ba-cta"
          />
          <div className="text-[10px] text-zinc-500 italic mt-1">
            💡 Pattern z Edward SEO (192 likes): GA4 panel "Without" 48 users vs "With" 25.4K users +31842% growth.
          </div>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 p-3 flex items-center justify-center">
          <canvas ref={canvasRef} className="max-w-full max-h-[600px] object-contain" data-testid="ad-creative-ba-canvas" />
        </div>
      </div>
    </div>
  );
}

function drawGa4Panel(ctx, x, y, w, h, title, metrics, trend, accentColor, lineColor, totalW, withDelta = false) {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.strokeRect(x, y, w, h);

  // Title
  ctx.fillStyle = "#111827";
  ctx.font = `900 ${Math.round(totalW * 0.034)}px Inter, system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(title, x + w / 2, y + h * 0.04);

  // KPI grid 4 columns
  const cells = metrics.slice(0, 4);
  const cellW = (w * 0.94) / 4;
  const cellY = y + h * 0.18;
  cells.forEach((m, i) => {
    const cellX = x + w * 0.03 + i * cellW;
    ctx.textAlign = "left";
    ctx.fillStyle = "#6B7280";
    ctx.font = `bold ${Math.round(totalW * 0.014)}px Inter, system-ui, sans-serif`;
    ctx.fillText(m.label, cellX, cellY);
    ctx.fillStyle = "#0a0a0a";
    ctx.font = `900 ${Math.round(totalW * 0.034)}px Inter, system-ui, sans-serif`;
    ctx.fillText(m.value, cellX, cellY + totalW * 0.022);
    if (withDelta && m.delta) {
      ctx.fillStyle = accentColor;
      ctx.font = `bold ${Math.round(totalW * 0.014)}px Inter, system-ui, sans-serif`;
      ctx.fillText("▲ " + m.delta, cellX, cellY + totalW * 0.06);
    }
  });

  // Trend chart
  const cy = y + h * 0.58;
  const ch = h * 0.36;
  const cx = x + w * 0.04;
  const cw = w * 0.92;
  ctx.strokeStyle = "#f3f4f6";
  ctx.lineWidth = 1;
  // gridlines
  for (let i = 1; i <= 4; i++) {
    const gy = cy + (ch / 5) * i;
    ctx.beginPath();
    ctx.moveTo(cx, gy);
    ctx.lineTo(cx + cw, gy);
    ctx.stroke();
  }
  drawSparkline(ctx, cx, cy, cw, ch, trend, lineColor);
}


// ===========================================================================
// V196 — StructuredCardEditor (shared canvas for coming_soon + competitor_killer)
// ===========================================================================
// Both variants share a dark-navy → blue gradient bg, a kicker line, a big
// headline, 4 blue-checkmark feature bullets, a domain or price-comparison
// footer band, and a CTA pill. The `variant` prop swaps the accent + footer.
function StructuredCardEditor({ preset, onChange, onSaved, variant }) {
  const canvasRef = useRef(null);
  const [aspect, setAspect] = useState("1:1");
  const [saving, setSaving] = useState(false);

  const update = (patch) => onChange({ ...preset, ...patch });
  const updateFeature = (idx, value) => {
    const next = [...(preset.features || [])];
    next[idx] = value;
    onChange({ ...preset, features: next });
  };

  useEffect(() => {
    if (canvasRef.current) renderStructuredCard(canvasRef.current, preset, aspect, variant);
  }, [preset, aspect, variant]);

  const handleDownload = () => {
    downloadCanvas(canvasRef.current, `agent-os-${variant}-${preset.lang}-${aspect}.png`);
  };

  const handleSave = async () => {
    setSaving(true);
    const png = canvasRef.current.toDataURL("image/png");
    const token = localStorage.getItem("admin_token") || "";
    try {
      await axios.post(
        `${API}/ad-creative/drafts`,
        { kind: variant, lang: preset.lang, title: preset.title, payload: preset, png_data_url: png },
        { headers: { "X-Admin-Token": token } },
      );
      onSaved();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const isKiller = variant === "competitor_killer";

  return (
    <div className="space-y-3" data-testid={`${variant}-editor`}>
      {/* Aspect ratio toggle */}
      <div className="flex items-center gap-2">
        {["1:1", "9:16", "16:9"].map((a) => (
          <button
            key={a}
            onClick={() => setAspect(a)}
            className={`text-xs px-3 py-1.5 border ${aspect === a
              ? "bg-amber-500 border-amber-400 text-black font-black"
              : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}
            data-testid={`${variant}-aspect-${a}`}
          >
            {a}
          </button>
        ))}
        <span className="flex-1" />
        <button onClick={handleDownload}
                className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider"
                data-testid={`${variant}-download`}>
          ⬇️ PNG
        </button>
        <button onClick={handleSave} disabled={saving}
                className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider disabled:opacity-50"
                data-testid={`${variant}-save`}>
          {saving ? "Ukladám…" : "💾 Save draft"}
        </button>
      </div>

      {/* Live canvas preview */}
      <canvas ref={canvasRef} className="w-full border border-zinc-800 bg-black rounded" />

      {/* Editor inputs */}
      <div className="grid md:grid-cols-2 gap-3 text-xs">
        <label className="space-y-1">
          <span className="text-zinc-500 uppercase tracking-widest font-black">Kicker</span>
          <input value={preset.kicker || ""}
                 onChange={(e) => update({ kicker: e.target.value })}
                 className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono"
                 data-testid={`${variant}-kicker`} />
        </label>
        <label className="space-y-1">
          <span className="text-zinc-500 uppercase tracking-widest font-black">Headline</span>
          <input value={preset.headline || ""}
                 onChange={(e) => update({ headline: e.target.value })}
                 className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono"
                 data-testid={`${variant}-headline`} />
        </label>
        {(preset.features || []).map((f, i) => (
          <label key={i} className="space-y-1 md:col-span-2">
            <span className="text-zinc-500 uppercase tracking-widest font-black">Feature {i + 1}</span>
            <input value={f}
                   onChange={(e) => updateFeature(i, e.target.value)}
                   className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono"
                   data-testid={`${variant}-feature-${i}`} />
          </label>
        ))}
        {isKiller ? (
          <>
            <label className="space-y-1">
              <span className="text-zinc-500 uppercase tracking-widest font-black">Competitor name</span>
              <input value={preset.competitor_name || ""}
                     onChange={(e) => update({ competitor_name: e.target.value })}
                     className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
            </label>
            <label className="space-y-1">
              <span className="text-zinc-500 uppercase tracking-widest font-black">Their price</span>
              <input value={preset.competitor_price || ""}
                     onChange={(e) => update({ competitor_price: e.target.value })}
                     className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
            </label>
            <label className="space-y-1">
              <span className="text-zinc-500 uppercase tracking-widest font-black">Our price</span>
              <input value={preset.our_price || ""}
                     onChange={(e) => update({ our_price: e.target.value })}
                     className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
            </label>
            <label className="space-y-1">
              <span className="text-zinc-500 uppercase tracking-widest font-black">Savings line</span>
              <input value={preset.savings_line || ""}
                     onChange={(e) => update({ savings_line: e.target.value })}
                     className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
            </label>
          </>
        ) : (
          <>
            <label className="space-y-1">
              <span className="text-zinc-500 uppercase tracking-widest font-black">Domain kicker</span>
              <input value={preset.domain_kicker || ""}
                     onChange={(e) => update({ domain_kicker: e.target.value })}
                     className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
            </label>
            <label className="space-y-1">
              <span className="text-zinc-500 uppercase tracking-widest font-black">Domain</span>
              <input value={preset.domain || ""}
                     onChange={(e) => update({ domain: e.target.value })}
                     className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono" />
            </label>
          </>
        )}
        <label className="space-y-1 md:col-span-2">
          <span className="text-zinc-500 uppercase tracking-widest font-black">CTA</span>
          <input value={preset.cta || ""}
                 onChange={(e) => update({ cta: e.target.value })}
                 className="w-full bg-black border border-zinc-700 text-zinc-200 px-2 py-1.5 font-mono"
                 data-testid={`${variant}-cta`} />
        </label>
      </div>
    </div>
  );
}


function renderStructuredCard(canvas, preset, aspect, variant) {
  const sizes = { "1:1": [1080, 1080], "9:16": [1080, 1920], "16:9": [1920, 1080] };
  const [W, H] = sizes[aspect] || sizes["1:1"];
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background gradient (dark navy → blue)
  const isKiller = variant === "competitor_killer";
  const grad = ctx.createLinearGradient(0, 0, W, H);
  if (isKiller) {
    grad.addColorStop(0, "#000000");
    grad.addColorStop(1, "#1c1917");
  } else {
    grad.addColorStop(0, "#020617");
    grad.addColorStop(0.6, "#0e1a4d");
    grad.addColorStop(1, "#1e3a8a");
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Subtle radial glow top-right
  const glow = ctx.createRadialGradient(W * 0.75, H * 0.15, 0, W * 0.75, H * 0.15, W * 0.55);
  glow.addColorStop(0, isKiller ? "rgba(59,130,246,0.20)" : "rgba(96,165,250,0.25)");
  glow.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  const accent = isKiller ? "#3B82F6" : "#60A5FA";
  const padX = W * 0.075;
  let y = H * 0.10;

  // Kicker (small uppercase tagline)
  ctx.fillStyle = accent;
  ctx.font = `900 ${Math.round(W * 0.025)}px -apple-system, "Segoe UI", system-ui, sans-serif`;
  ctx.textAlign = "left";
  ctx.fillText(preset.kicker || "", padX, y);
  y += W * 0.035;

  // Headline (big bold)
  ctx.fillStyle = "#ffffff";
  const headlineSize = Math.round(W * 0.062);
  ctx.font = `900 ${headlineSize}px -apple-system, "Segoe UI Black", system-ui, sans-serif`;
  wrapTextLeft(ctx, preset.headline || "", padX, y, W - padX * 2, headlineSize * 1.1);
  // estimate lines to advance y
  const headlineLines = estimateLines(ctx, preset.headline || "", W - padX * 2);
  y += headlineLines * headlineSize * 1.1 + W * 0.04;

  // Features — 4 blue-checkmark bullets
  const features = (preset.features || []).slice(0, 4);
  const featSize = Math.round(W * 0.027);
  ctx.font = `600 ${featSize}px -apple-system, "Segoe UI", system-ui, sans-serif`;
  features.forEach((f) => {
    // Checkmark badge
    const cx = padX + featSize * 0.6;
    const cy = y - featSize * 0.35;
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(cx, cy, featSize * 0.6, 0, Math.PI * 2);
    ctx.fill();
    // White check
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = Math.max(2, featSize * 0.18);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(cx - featSize * 0.25, cy);
    ctx.lineTo(cx - featSize * 0.05, cy + featSize * 0.22);
    ctx.lineTo(cx + featSize * 0.30, cy - featSize * 0.22);
    ctx.stroke();
    // Label
    ctx.fillStyle = "#e5e7eb";
    ctx.font = `600 ${featSize}px -apple-system, "Segoe UI", system-ui, sans-serif`;
    ctx.fillText(f, cx + featSize * 1.1, y);
    y += featSize * 1.7;
  });

  // Footer band (price comparison for killer, domain badge for coming_soon)
  y += W * 0.025;
  if (isKiller) {
    // Price comparison: STRIKED competitor price → our price
    const priceSize = Math.round(W * 0.038);
    ctx.font = `900 ${priceSize}px -apple-system, "Segoe UI Black", system-ui, sans-serif`;
    ctx.fillStyle = "#f87171";
    ctx.fillText(preset.competitor_name || "", padX, y);
    const compNameW = ctx.measureText(preset.competitor_name || "").width;
    ctx.fillStyle = "#ef4444";
    ctx.fillText(" " + (preset.competitor_price || ""), padX + compNameW, y);
    // Strike-through across competitor price
    const compPriceW = ctx.measureText(" " + (preset.competitor_price || "")).width;
    ctx.strokeStyle = "#ef4444";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(padX + compNameW + 4, y - priceSize * 0.35);
    ctx.lineTo(padX + compNameW + compPriceW - 4, y - priceSize * 0.35);
    ctx.stroke();
    // → Our price
    ctx.fillStyle = "#10b981";
    const sep = "  →  ";
    ctx.fillText(sep, padX + compNameW + compPriceW, y);
    const sepW = ctx.measureText(sep).width;
    ctx.fillStyle = "#34d399";
    ctx.fillText("Agent OS " + (preset.our_price || ""), padX + compNameW + compPriceW + sepW, y);
    y += priceSize * 1.4;
    // Savings line
    if (preset.savings_line) {
      ctx.font = `500 ${Math.round(W * 0.022)}px -apple-system, system-ui, sans-serif`;
      ctx.fillStyle = "#a3a3a3";
      ctx.fillText(preset.savings_line, padX, y);
      y += W * 0.04;
    }
  } else {
    // Domain badge
    ctx.font = `500 ${Math.round(W * 0.022)}px -apple-system, system-ui, sans-serif`;
    ctx.fillStyle = "#94a3b8";
    ctx.fillText(preset.domain_kicker || "", padX, y);
    y += W * 0.035;
    ctx.font = `900 ${Math.round(W * 0.034)}px -apple-system, system-ui, sans-serif`;
    // Color the "moja" vs ".sk" pattern for visual interest
    ctx.fillStyle = accent;
    ctx.fillText("🚀 ", padX, y);
    const rocketW = ctx.measureText("🚀 ").width;
    ctx.fillStyle = "#ffffff";
    ctx.fillText(preset.domain || "", padX + rocketW, y);
    y += W * 0.06;
  }

  // CTA pill at bottom
  const ctaText = preset.cta || "ZÍSKAJ AGENT OS →";
  const ctaY = H - W * 0.075;
  ctx.font = `900 ${Math.round(W * 0.032)}px -apple-system, system-ui, sans-serif`;
  const ctaW = ctx.measureText(ctaText).width + W * 0.10;
  const ctaH = W * 0.075;
  const ctaX = (W - ctaW) / 2;
  ctx.fillStyle = isKiller ? "#3B82F6" : "#60A5FA";
  roundRect(ctx, ctaX, ctaY - ctaH * 0.65, ctaW, ctaH, ctaH * 0.5);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.fillText(ctaText, W / 2, ctaY);
  ctx.textAlign = "left";

  // Watermark "MIKA · AGENT OS · toprankpulse.com"
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.font = `700 ${Math.round(W * 0.015)}px -apple-system, system-ui, sans-serif`;
  ctx.textAlign = "right";
  ctx.fillText("MIKA · AGENT OS · toprankpulse.com", W - padX, H - W * 0.025);
  ctx.textAlign = "left";
}

function wrapTextLeft(ctx, text, x, y, maxWidth, lineHeight) {
  if (!text) return;
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (let i = 0; i < words.length; i++) {
    const test = line + words[i] + " ";
    if (ctx.measureText(test).width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, yy);
      line = words[i] + " ";
      yy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line.trim(), x, yy);
}

function estimateLines(ctx, text, maxWidth) {
  if (!text) return 1;
  const words = text.split(" ");
  let line = "";
  let count = 1;
  for (let i = 0; i < words.length; i++) {
    const test = line + words[i] + " ";
    if (ctx.measureText(test).width > maxWidth && i > 0) {
      count++;
      line = words[i] + " ";
    } else {
      line = test;
    }
  }
  return count;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
